import React, {RefObject} from "react";
import {connect} from "react-redux";
import classNames from "classnames";
import {
    Button, Dropdown, Layout, Menu,
} from "antd";
import {User, UserManager} from "oidc-client";
import {DownOutlined, GlobalOutlined, LogoutOutlined} from "@ant-design/icons";
import {
    getActiveLanguage, Language, LocalizeState, setActiveLanguage, Translate,
} from "react-localize-redux";
import {DisplayType, EditorType, Nullable} from "@data-force/app";
import StoreState from "../../interfaces/store/storeState";
import "./AccountBar.scss";
import HistoryParams from "../../interfaces/historyParams";
import {getHistoryParams} from "../../utils/common";
import {SetUserMenuOpenKeysAction, SetUserMenuVisibleAction} from "../../interfaces/store/actions/layout";
import {setUserMenuOpenKeys, setUserMenuVisible} from "../../store/actions/layout";
import {
    ARROW_DOWN_KEY, ARROW_LEFT_KEY, ARROW_RIGHT_KEY, ARROW_UP_KEY, ENTER_KEY, ESCAPE_KEY, SPACE_KEY, TAB_KEY,
} from "../../utils/constants";

interface DispatchProps {
    setActiveLanguage(languageCode: string): void

    setUserMenuOpenKeys(payload: string[]): SetUserMenuOpenKeysAction

    setUserMenuVisible(payload: boolean): SetUserMenuVisibleAction
}

interface StateProps {
    user: Nullable<User>
    userManager: Nullable<UserManager>
    localize: LocalizeState
    activeLanguage: Language
    displayType: DisplayType
    userMenuOpenKeys: string[]
    userMenuVisible: boolean
}

export type Props = StateProps & DispatchProps;
// TODO Vladica: new accessible version of antd Menu should be used, as soon as it becomes available (https://github.com/ant-design/ant-design/pull/30382)
class AccountBar extends React.Component<Props> {
    private static readonly translatePath: string = "components.AccountBar.AccountBar";

    private static readonly languageSubmenuKey: string = "sub1";

    private languagesMenu: RefObject<HTMLSpanElement> = React.createRef();

    private languageItemRefs: RefObject<HTMLSpanElement>[] = [];

    private logoutItem: RefObject<HTMLSpanElement> = React.createRef();

    private accountButton: RefObject<HTMLButtonElement> = React.createRef();

    constructor(props: Props) {
        super(props);

        this.handleToggle = this.handleToggle.bind(this);
        this.accountButtonHandler = this.accountButtonHandler.bind(this);
        this.onVisibleChange = this.onVisibleChange.bind(this);
        this.focusLanguageItem = this.focusLanguageItem.bind(this);
        this.logoutKeyDownHandler = this.logoutKeyDownHandler.bind(this);
        this.logout = this.logout.bind(this);
        this.closeMenu = this.closeMenu.bind(this);
        this.getLanguageSubmenu = this.getLanguageSubmenu.bind(this);
        this.getInactiveLanguageIndex = this.getInactiveLanguageIndex.bind(this);
    }

    componentDidMount() {
        const {localize}: Props = this.props;
        if (localize?.languages) {
            this.languageItemRefs = localize.languages.map(() => React.createRef());
        }
    }

    shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
        const {
            user, activeLanguage, userMenuOpenKeys, userMenuVisible,
        }: Props = this.props;
        const userChanged: boolean = nextProps.user?.profile.name !== user?.profile.name;
        const languageChanged: boolean = nextProps.activeLanguage?.code !== activeLanguage?.code;
        const userMenuOpenKeysChanged: boolean = nextProps.userMenuOpenKeys !== userMenuOpenKeys;
        const userMenuVisibilityChange: boolean = nextProps.userMenuVisible !== userMenuVisible;
        return userChanged || languageChanged || userMenuOpenKeysChanged || userMenuVisibilityChange;
    }

    /**
     * Handles visibility of "user menu".
     *
     * @param {boolean} visible
     * @return {void}
     */
    onVisibleChange(visible: boolean): void {
        this.props.setUserMenuVisible(visible);
    }

    /**
     *  Selects language, in "languages" submenu
     *
     * @param {string} languageCode
     * @return {void}
     */
    onSelectItem(languageCode: string): void {
        this.props.setActiveLanguage(languageCode);
        this.props.setUserMenuVisible(false);
    }

    /**
     * Get language submenu focusable element
     *
     * @return {Nullable<HTMLElement>}
     */
    getLanguageSubmenu(): Nullable<HTMLElement> {
        return this.languagesMenu.current?.parentElement?.parentElement?.parentElement;
    }

    /**
     * Get index of the first inactive language
     *
     * @return {number}
     */
    getInactiveLanguageIndex(): number {
        const {localize}: Props = this.props;
        let languageIndex: number = -1;
        if (localize?.languages?.length) {
            languageIndex = localize.languages.findIndex(
                (language: Language): boolean => !language.active,
            );
        }
        return languageIndex;
    }

    /**
     * Handles keyboard events, in "languages" submenu items.
     *
     * @param {string} languageCode
     * @param {number} index
     * @param {React.KeyboardEvent<HTMLLIElement>} event
     * @return {void}
     */
    itemKeyDownHandler(languageCode: string, index: number, event: React.KeyboardEvent<HTMLLIElement>): void {
        switch (event.key) {
            case ENTER_KEY:
            case SPACE_KEY:
                this.props.setActiveLanguage(languageCode);
                this.props.setUserMenuVisible(false);
                event.stopPropagation();
                break;
            case ARROW_LEFT_KEY:
            case ARROW_RIGHT_KEY:
                this.handleToggle([]);
                this.getLanguageSubmenu()?.focus();
                event.stopPropagation();
                break;
            case ARROW_UP_KEY:
                if (index > 0) {
                    this.focusLanguageItem(index - 1);
                    event.stopPropagation();
                }
                break;
            case ARROW_DOWN_KEY:
                if (index < this.languageItemRefs.length - 1) {
                    this.focusLanguageItem(index + 1);
                    event.stopPropagation();
                }
                break;
            case ESCAPE_KEY:
                this.handleToggle([]);
                this.getLanguageSubmenu()?.focus();
                event.stopPropagation();
                break;
            case TAB_KEY:
                if (index === this.languageItemRefs.length - 1) {
                    this.handleToggle([]);
                }
                break;
            default:
                break;
        }
    }

    /**
     * Handles keyboard events on user menu button.
     *
     * @param {React.KeyboardEvent<HTMLDivElement>} event
     * @return {void}
     */
    accountButtonHandler(event: React.KeyboardEvent<HTMLDivElement>): void {
        switch (event.key) {
            case SPACE_KEY:
                this.props.setUserMenuOpenKeys([]);
                event.stopPropagation();
                break;
            case ARROW_DOWN_KEY:
                this.getLanguageSubmenu()?.focus();
                event.stopPropagation();
                break;
            default:
                break;
        }
    }

    /**
     * Controls visibility of user menu submenus.
     *
     * @param {React.ReactText[]} openKeys
     * @return {void}
     */
    handleToggle(openKeys: React.ReactText[]): void {
        const keys: string[] = openKeys?.length ? openKeys as string[] : [];
        this.props.setUserMenuOpenKeys(keys);
    }

    /**
     * Handles keyboard events on "languages" submenu.
     *
     * @param {React.ReactText[]} openKeys
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    handleSubmenu(openKeys: React.ReactText[], event: React.KeyboardEvent): void {
        const {userMenuOpenKeys}: Props = this.props;
        const {languageSubmenuKey}: typeof AccountBar = AccountBar;
        switch (event.key) {
            case ENTER_KEY:
            case SPACE_KEY:
            case ARROW_LEFT_KEY:
            case ARROW_RIGHT_KEY:
                this.handleToggle(openKeys);
                event.stopPropagation();
                break;
            case ARROW_DOWN_KEY:
                if (userMenuOpenKeys.includes(languageSubmenuKey)) {
                    const inactiveLanguageIndex: number = this.getInactiveLanguageIndex();
                    if (inactiveLanguageIndex > -1) {
                        this.languageItemRefs[inactiveLanguageIndex]?.current?.parentElement?.parentElement?.focus();
                    }
                } else {
                    this.handleToggle([]);
                    this.logoutItem.current?.parentElement?.parentElement?.focus();
                }
                event.stopPropagation();
                break;
            case ESCAPE_KEY:
                if (userMenuOpenKeys.includes(languageSubmenuKey)) {
                    this.handleToggle([]);
                    this.getLanguageSubmenu()?.focus();
                } else {
                    this.closeMenu(event);
                }
                break;
            default:
                break;
        }
    }

    /**
     * Handles keyboard events on "logout" menu item.
     *
     * @param {React.KeyboardEvent<HTMLLIElement>} event
     * @return {void}
     */
    logoutKeyDownHandler(event: React.KeyboardEvent<HTMLLIElement>): void {
        switch (event.key) {
            case ENTER_KEY:
            case SPACE_KEY:
                this.logout();
                event.stopPropagation();
                break;
            case ARROW_UP_KEY:
                this.getLanguageSubmenu()?.focus();
                event.stopPropagation();
                break;
            case ESCAPE_KEY:
                this.closeMenu(event);
                break;
            default:
                break;
        }
    }

    /**
     * Triggers logout action
     *
     * @return {void}
     */
    logout(): void {
        // TODO handle promise properly
        this.props.userManager!.signoutRedirect().catch();
    }

    /**
     * Focus item in languages submenu.
     *
     * @param index
     * @return {void}
     */
    focusLanguageItem(index: number): void {
        const nextItem: Nullable<HTMLSpanElement> = this.languageItemRefs[index].current;
        nextItem?.parentElement?.parentElement?.focus();
    }

    /**
     * Close user menu
     *
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    closeMenu(event: React.KeyboardEvent): void {
        this.props.setUserMenuVisible(false);
        this.accountButton.current?.focus();
        event.stopPropagation();
    }

    render(): React.ReactNode {
        const {
            user, localize, displayType, userMenuOpenKeys, userMenuVisible,
        }: Props = this.props;
        const {languageSubmenuKey, translatePath}: typeof AccountBar = AccountBar;
        const isDictation: boolean = displayType === DisplayType.DICTATION;
        const headerTitleClasses: string = classNames({
            "df-header-title": true,
            "dictation-header-title": isDictation,
        });

        return (
            <Layout.Header className="df-header">
                <h1 className={headerTitleClasses}>
                    <Translate id={`${translatePath}.${isDictation ? "dictation" : "intaker"}.text`}/>
                </h1>
                <Dropdown
                    visible={userMenuVisible}
                    onVisibleChange={this.onVisibleChange}
                    trigger={["click", "hover"]}
                    getPopupContainer={(triggerNode: HTMLElement) => triggerNode as HTMLElement}
                    overlay={(
                        <Menu
                            className="df-account-menu"
                            getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentNode as HTMLElement}
                            tabIndex={-1}
                            openKeys={userMenuOpenKeys}
                            onOpenChange={this.handleToggle}
                        >
                            <Menu.SubMenu
                                key={languageSubmenuKey}
                                // @ts-ignore
                                tabIndex={-1}
                                title={(
                                    <span ref={this.languagesMenu}>
                                        <GlobalOutlined/>
                                        {" "}
                                        <Translate id={`${translatePath}.languages.text`}/>
                                    </span>
                                )}
                                onKeyDown={this.handleSubmenu.bind(this, [languageSubmenuKey])}
                            >
                                {
                                    localize?.languages?.map((item: Language, index: number): React.ReactNode => {
                                        const {name, code, active}: Language = item;
                                        return (
                                            <Menu.Item
                                                key={code + index}
                                                disabled={active}
                                                onClick={this.onSelectItem.bind(this, code)}
                                                onKeyDown={this.itemKeyDownHandler.bind(this, code, index)}
                                                role="button"
                                                tabIndex={-1}
                                            >
                                                <span ref={this.languageItemRefs[index]}>{name}</span>
                                            </Menu.Item>
                                        );
                                    })
                                }
                            </Menu.SubMenu>
                            <Menu.Item
                                key="logout"
                                onClick={this.logout}
                                tabIndex={-1}
                                onKeyDown={this.logoutKeyDownHandler}
                            >
                                <span ref={this.logoutItem}>
                                    <LogoutOutlined/>
                                    <Translate id={`${translatePath}.logout.text`}/>
                                </span>
                            </Menu.Item>
                        </Menu>
                    )}
                >
                    <Button
                        data-testid="user"
                        className="df-account-btn"
                        tabIndex={0}
                        onKeyDown={this.accountButtonHandler}
                        ref={this.accountButton}
                    >
                        {user?.profile.name}
                        {" "}
                        <DownOutlined/>
                    </Button>
                </Dropdown>
            </Layout.Header>
        );
    }
}

function mapStateToProps({
    localize,
    userManager,
    oidc,
    project,
    schema: {parsedSchemas},
    layout: {header: {userMenu: {openKeys, visible}}},
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const editor: EditorType = parsedSchemas[schemaKey] as EditorType;

    return {
        user: oidc.user,
        userManager,
        localize,
        activeLanguage: getActiveLanguage(localize),
        displayType: editor?.layout || DisplayType.INTAKER_WIZARD,
        userMenuOpenKeys: openKeys,
        userMenuVisible: visible,
    };
}

const mapDispatchToProps: DispatchProps = {
    setActiveLanguage,
    setUserMenuOpenKeys,
    setUserMenuVisible,
};

export {AccountBar};
export default connect(mapStateToProps, mapDispatchToProps)(AccountBar);
